/** Standalone station tab pages — Blueprint Part 7 promotion.
 *
 *  Each tab inside StationDash gets a real /app/* route so it has its own
 *  URL, browser history, breadcrumb, and is reachable from the sidebar.
 *  We reuse the existing tab components from StationDashSetupTabs to avoid
 *  rebuilding the editors — this is a pure URL-promotion move.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RateCardTab, ScheduleTab, ProfileTab, AdLogTab,
} from "@/pages/StationDashSetupTabs";
import { api } from "@/lib/api";
import { PageHeader, Skeleton, EmptyState, Button } from "@/design";

function useActiveStation() {
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/my-stations");
      const list = data?.stations || [];
      if (list.length === 0) {
        setStation(null);
        return;
      }
      const saved = localStorage.getItem("ws_active_station_id");
      const found = list.find((s) => s.id === saved) || list[0];
      setStation(found);
      localStorage.setItem("ws_active_station_id", found.id);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Re-sync when the AppShell topbar switches stations
    const h = () => refresh();
    window.addEventListener("ws:station-changed", h);
    return () => window.removeEventListener("ws:station-changed", h);
  }, []);

  return { station, loading, refresh };
}

function StationTabWrap({ title, eyebrow, helper, Component }) {
  const { station, loading, refresh } = useActiveStation();
  const nav = useNavigate();

  if (loading) {
    return (
      <div data-testid="station-tab-loading">
        <PageHeader tone="dark" eyebrow={eyebrow} title={title} />
        <Skeleton variant="card" tone="dark" height={200} />
      </div>
    );
  }
  if (!station) {
    return (
      <EmptyState tone="dark"
        title="Claim a station first"
        helper="This page needs an active station. Claim yours in 90 seconds and you'll land back here."
        action={<Button variant="primary" audience="station" onClick={() => nav("/claim")}>Claim a station →</Button>}
      />
    );
  }
  return (
    <div>
      <PageHeader tone="dark" eyebrow={eyebrow} title={`${title} · ${station.name}`} subtitle={helper} />
      <Component station={station} onSaved={() => { toast.success(`${title} saved`); refresh(); }} />
    </div>
  );
}

export function RateCardPage() {
  return <StationTabWrap title="Rate Card" eyebrow="STATION"
    helper="Build & price your Sponsor / Standard / Premium tiers. Agencies use this in the Planning Tool to compute CPTA."
    Component={RateCardTab} />;
}
export function SchedulePage() {
  return <StationTabWrap title="Programme Schedule" eyebrow="STATION"
    helper="Manage your weekly grid by daypart. Named shows surface on dashboards, certificates, and reports."
    Component={ScheduleTab} />;
}
export function ProfilePage() {
  return <StationTabWrap title="Station Profile" eyebrow="STATION"
    helper="Your public page lives at /stations/{your-id}. Logo, tagline, social links — make it agency-ready."
    Component={ProfileTab} />;
}
export function AdLogPage() {
  return <StationTabWrap title="Ad Log Upload" eyebrow="STATION"
    helper="CSV upload of daily ad spots. Drives auto-certificate matching for every airing."
    Component={AdLogTab} />;
}
